/**
 * usePendingMeetings — meetings awaiting an accept/dismiss decision, backing
 * MeetingKbBanner. Source-agnostic: Granola (useGranolaPending) and Teams
 * (MeetingsApi) both use it, since the review flow only needs ids and the
 * three calls below.
 *
 * Modeled on useProposals: fetches pending meetings once on mount (no
 * polling, no SSE — accepting/dismissing is a user-driven action, and a
 * fresh page load re-fetches naturally), and exposes optimistic
 * accept/dismiss so the banner updates instantly.
 *
 *  - Never throws from the initial fetch: failures are logged, surfaced as
 *    `error`, and recoverable via `retry` — the review workflow must stay
 *    visible (and retryable) rather than silently disappearing when the
 *    initial load fails.
 *  - accept/dismiss DO throw on failure (after restoring the optimistic
 *    removal) so the caller can show a toast, matching useProposals.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { GranolaApi, GranolaPendingMeeting } from "../../api/granolaApi";

/** The calls a pending-meeting source must offer. */
export interface PendingMeetingsSource<M extends { id: string }> {
  listPending(): Promise<M[]>;
  accept(id: string): Promise<unknown>;
  dismiss(id: string): Promise<unknown>;
}

export interface UsePendingMeetingsResult<M extends { id: string }> {
  /** Pending meetings awaiting a decision (optimistically updated). */
  meetings: M[];
  /** True while the initial fetch is in flight. */
  loading: boolean;
  /** Last fetch error, if any. Cleared on the next successful fetch. */
  error: string | null;
  /** Re-attempt loading pending meetings after a failed fetch. */
  retry: () => void;
  /** Accept a meeting (what that does is the source's business). Optimistically
   *  removes it from the list; restores on failure. */
  accept: (id: string) => Promise<void>;
  /** Dismiss a meeting. Same optimistic behaviour as accept. */
  dismiss: (id: string) => Promise<void>;
}

export type UseGranolaPendingResult = UsePendingMeetingsResult<GranolaPendingMeeting>;

export function useGranolaPending(
  granolaApi: GranolaApi | null | undefined,
): UseGranolaPendingResult {
  return usePendingMeetings(granolaApi);
}

export function usePendingMeetings<M extends { id: string }>(
  source: PendingMeetingsSource<M> | null | undefined,
): UsePendingMeetingsResult<M> {
  const [meetings, setMeetings] = useState<M[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Identifies the most recently started fetch. A stale request (its
  // source replaced by a new one before it resolved, e.g. on an apiUrl
  // change) checks this before touching state so its late result/error
  // can't clobber the replacement client's data.
  const requestIdRef = useRef(0);

  // Mirrors the last committed `meetings` for accept/dismiss to read
  // synchronously (see below) without needing `meetings` in their deps.
  const meetingsRef = useRef(meetings);
  useEffect(() => {
    meetingsRef.current = meetings;
  }, [meetings]);

  // Identifies the current source client. Bumped whenever source
  // changes, including to/from null. accept/dismiss capture this at call
  // time so that if their client is replaced before the request settles,
  // their failure handler can detect it's no longer current and skip
  // restoring optimistic state into what is now a different client's list.
  const generationRef = useRef(0);
  useEffect(() => {
    generationRef.current += 1;
    // Pending rows belong to a specific client identity. On any client
    // transition — including to null — invalidate requestIdRef so a
    // listPending() still in flight for the previous client can't write
    // its late response into the new (or absent) client's list, and clear
    // the rows immediately rather than leaving the old client's data
    // visible until (or if) a new fetch resolves.
    requestIdRef.current += 1;
    setMeetings([]);
    setError(null);
    setLoading(false);
  }, [source]);

  const loadPending = useCallback(async () => {
    if (!source) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const rows = await source.listPending();
      if (requestIdRef.current !== requestId) return;
      setMeetings(rows);
      setError(null);
    } catch (exc) {
      if (requestIdRef.current !== requestId) return;
      // Contextual, not the raw exception — a rejected fetch/response body
      // could carry sensitive detail we don't want in the browser console.
      console.error("useGranolaPending: failed to load pending meetings", {
        message: exc instanceof Error ? exc.message : String(exc),
      });
      setError(exc instanceof Error ? exc.message : "Failed to load pending meetings");
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [source]);

  // Initial fetch on mount (or when source becomes available).
  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const accept = useCallback(
    async (id: string) => {
      if (!source) return;
      // Optimistic: remove from list immediately. `removed` comes from the
      // ref (mirrors the last committed `meetings`, kept in sync above)
      // rather than `meetings` itself or a variable assigned inside the
      // setMeetings updater below — React doesn't guarantee that updater
      // runs before this point, and reading `meetings` directly would put
      // it in this callback's deps and risk restoring a stale snapshot.
      const generation = generationRef.current;
      const removed = meetingsRef.current.find((m) => m.id === id);
      setMeetings((prev) => prev.filter((m) => m.id !== id));
      try {
        await source.accept(id);
      } catch (exc) {
        // Restore only the failed meeting on failure, and only if this is
        // still the active client's request — a stale restore would inject
        // a removed-elsewhere meeting into a different client's list.
        if (generationRef.current === generation) {
          if (removed) {
            setMeetings((prev) =>
              prev.some((m) => m.id === id) ? prev : [...prev, removed],
            );
          }
          const msg = exc instanceof Error ? exc.message : "Failed to accept meeting";
          setError(msg);
        }
        throw exc;
      }
    },
    [source],
  );

  const dismiss = useCallback(
    async (id: string) => {
      if (!source) return;
      const generation = generationRef.current;
      const removed = meetingsRef.current.find((m) => m.id === id);
      setMeetings((prev) => prev.filter((m) => m.id !== id));
      try {
        await source.dismiss(id);
      } catch (exc) {
        if (generationRef.current === generation) {
          if (removed) {
            setMeetings((prev) =>
              prev.some((m) => m.id === id) ? prev : [...prev, removed],
            );
          }
          const msg = exc instanceof Error ? exc.message : "Failed to dismiss meeting";
          setError(msg);
        }
        throw exc;
      }
    },
    [source],
  );

  const retry = useCallback(() => {
    void loadPending();
  }, [loadPending]);

  return {
    meetings,
    loading,
    error,
    retry,
    accept,
    dismiss,
  };
}
